import React, { useCallback, useEffect, useState } from 'react';
import { Ban, Search, X } from 'lucide-react';
import { ConfirmModal, GovernanceBreadcrumbs, RiskBadge, StatusBadge, btnPrimary, btnSecondary, inputClass, selectClass } from '../components/shared';
import { aiGovernanceService, OverrideItem, PolicyListItem } from '../../../../components/api/aiGovernanceService';

export default function OverridesScreen() {
  const [search, setSearch] = useState('');
  const [revokeTarget, setRevokeTarget] = useState<{ id: string; scope: string } | null>(null);
  const [rows, setRows] = useState<OverrideItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [policies, setPolicies] = useState<PolicyListItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    scope: '',
    scopeType: 'User',
    policyId: '',
    exceptionType: 'Temporary Access',
    reason: '',
    expiryDate: '',
    riskLevel: 'Medium',
  });

  const fetchOverrides = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await aiGovernanceService.getOverrides({
        search: search || undefined,
        page: 1,
        pageSize: 100,
      });
      setRows(res.items || []);
    } catch {
      setError('Failed to load overrides.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchOverrides();
  }, [fetchOverrides]);

  useEffect(() => {
    (async () => {
      try {
        const res = await aiGovernanceService.getPolicies({ page: 1, pageSize: 100 });
        setPolicies(res.items || []);
      } catch {
        // ignore
      }
    })();
  }, []);

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    try {
      await aiGovernanceService.revokeOverride(revokeTarget.id, 'Revoked from portal');
      setRevokeTarget(null);
      await fetchOverrides();
    } catch {
      setError('Failed to revoke override.');
      setRevokeTarget(null);
    }
  };

  const handleCreate = async () => {
    if (!form.scope || !form.policyId || !form.reason || !form.expiryDate) {
      setError('Scope, policy, reason, and expiry date are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await aiGovernanceService.createOverride(form);
      setShowCreate(false);
      await fetchOverrides();
    } catch {
      setError('Failed to create override.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <GovernanceBreadcrumbs items={[{ label: 'AI Governance' }, { label: 'Overrides / Exceptions' }]} />

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Policy Overrides & Exceptions</h1>
          <p className="text-xs text-slate-500 mt-0.5">Manage temporary policy exceptions — all overrides are audited and time-bound.</p>
        </div>
        <button onClick={() => setShowCreate(true)} className={btnSecondary}>Request Exception</button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2">{error}</div>}

      <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-[11px] text-amber-800">
        Exceptions are temporary, audited, and cannot disable platform-level security controls.
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search overrides..." className={`${inputClass} pl-8`} />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              {['Scope', 'Type', 'Policy', 'Exception Type', 'Reason', 'Approved By', 'Expiry Date', 'Status', 'Risk', 'Actions'].map((h) => (
                <th key={h} className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} className="px-3 py-8 text-center text-slate-500">Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={10} className="px-3 py-8 text-center text-slate-500">No overrides found.</td></tr>
            ) : rows.map((row) => (
              <tr key={row.id} className="border-b border-slate-100 hover:bg-slate-50/80">
                <td className="px-3 py-2">
                  <p className="font-medium text-slate-800">{row.scope}</p>
                  <p className="text-[10px] text-slate-400">{row.scopeType}</p>
                </td>
                <td className="px-3 py-2 text-slate-600">{row.scopeType}</td>
                <td className="px-3 py-2 text-indigo-700 font-medium">{row.policy}</td>
                <td className="px-3 py-2 text-slate-600">{row.exceptionType}</td>
                <td className="px-3 py-2 text-slate-600 max-w-[160px] truncate">{row.reason}</td>
                <td className="px-3 py-2 text-slate-600">{row.approvedBy || '—'}</td>
                <td className="px-3 py-2 text-slate-500">{row.expiryDate}</td>
                <td className="px-3 py-2"><StatusBadge status={row.status} /></td>
                <td className="px-3 py-2"><RiskBadge level={row.riskLevel} /></td>
                <td className="px-3 py-2">
                  {(row.status === 'Active' || row.status === 'Pending') && (
                    <button onClick={() => setRevokeTarget({ id: row.id, scope: row.scope })} className="flex items-center gap-1 text-[10px] font-medium text-red-600 hover:text-red-800">
                      <Ban className="w-3 h-3" /> Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-900">Request Exception</h3>
              <button onClick={() => setShowCreate(false)} className="p-1 rounded hover:bg-slate-100 text-slate-400"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1">Scope</label>
                <input value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} className={inputClass} placeholder="user@acme.com" />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1">Scope Type</label>
                <select value={form.scopeType} onChange={(e) => setForm({ ...form, scopeType: e.target.value })} className={selectClass}>
                  <option>User</option><option>Group</option><option>Department</option><option>Resource</option><option>Workflow</option><option>Knowledge Base</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1">Policy</label>
                <select value={form.policyId} onChange={(e) => setForm({ ...form, policyId: e.target.value })} className={selectClass}>
                  <option value="">Select policy</option>
                  {policies.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1">Exception Type</label>
                <select value={form.exceptionType} onChange={(e) => setForm({ ...form, exceptionType: e.target.value })} className={selectClass}>
                  <option>Temporary Access</option><option>Tool Allowlist</option><option>Model Override</option><option>Data Classification</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1">Reason</label>
                <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1">Expiry Date</label>
                <input type="date" value={form.expiryDate} onChange={(e) => setForm({ ...form, expiryDate: e.target.value })} className={inputClass} />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowCreate(false)} className={btnSecondary}>Cancel</button>
              <button onClick={handleCreate} disabled={saving} className={btnPrimary}>{saving ? 'Saving...' : 'Submit'}</button>
            </div>
          </div>
        </div>
      )}

      {revokeTarget && (
        <ConfirmModal
          title="Revoke Exception"
          message={`Revoke the active override for "${revokeTarget.scope}"? This will immediately restore the original policy enforcement.`}
          confirmLabel="Revoke Override"
          onConfirm={handleRevoke}
          onCancel={() => setRevokeTarget(null)}
        />
      )}
    </div>
  );
}
